import React, { useMemo, useState, useEffect } from "react";
import swal from 'sweetalert';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Input,
  UncontrolledTooltip,
  Badge
} from "reactstrap";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import TableContainer from "../../components/Common/TableContainer";
import CardPagination from "../../components/Common/CardPagination";
import { getAllvisas, delVisa, putVisa } from "helpers/requests";
import { createPayement } from "helpers/requests"; // Import the payment creation function
import PaymentFormModal from "../../components/Common/PaymentFormModal";
import { format, parseISO } from 'date-fns';
import { Oval } from "react-loader-spinner";

const VisaPage = (props) => {
  useEffect(() => {
    document.title = "Visa Page";
  }, []);

  const [visas, setVisas] = useState([]);
  const [filteredVisas, setFilteredVisas] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Payment modal
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null);

  // New state for filtering and searching
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem("visa_search") || "");
  const [statusFilter, setStatusFilter] = useState(() => localStorage.getItem("visa_status") || "all");
  const [monthFilter, setMonthFilter] = useState(() => localStorage.getItem("visa_month") || "all");
  const [yearFilter, setYearFilter] = useState(() => localStorage.getItem("visa_year") || "all");
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("visa_sort") || "default");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => parseInt(localStorage.getItem("visa_pageSize") || "12", 10));

  // Persist filters
  useEffect(() => {
    localStorage.setItem("visa_search", searchTerm);
    localStorage.setItem("visa_status", statusFilter);
    localStorage.setItem("visa_month", monthFilter);
    localStorage.setItem("visa_year", yearFilter);
    localStorage.setItem("visa_sort", sortBy);
    localStorage.setItem("visa_pageSize", String(pageSize));
  }, [searchTerm, statusFilter, monthFilter, yearFilter, sortBy, pageSize]);

  // Reset to first page whenever filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, monthFilter, yearFilter, sortBy, pageSize]);

  // Generate years for dropdown (current year and previous 5 years)
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return ["all", ...Array.from({ length: 5 }, (_, i) => currentYear - i)];
  }, []);

  // Generate months for dropdown
  const months = useMemo(() => [
    "all", "January", "February", "March", "April", "May", "June", 
    "July", "August", "September", "October", "November", "December"
  ], []);

  // Fetch visas using getAllvisas function
  const fetchVisas = async () => {
    try {
      const response = await getAllvisas();
      setVisas(response);
      setFilteredVisas(response);
    } catch (error) {
      console.error("Error fetching visas:", error);
      swal("Error!", "Could not fetch visa data.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVisas();
  }, []);

  // Advanced filtering and sorting effect
  useEffect(() => {
    let updatedVisas = [...visas];

    // Filter by status
    if (statusFilter !== "all") {
      updatedVisas = updatedVisas.filter(
        (visa) => visa.status === statusFilter
      );
    }

    // Filter by month of createdAt
    if (monthFilter !== "all") {
      const monthIndex = months.indexOf(monthFilter) - 1;
      updatedVisas = updatedVisas.filter((visa) => {
        const visaDate = new Date(visa.createdAt);
        return visaDate.getMonth() === monthIndex;
      });
    }

    // Filter by year of createdAt
    if (yearFilter !== "all") {
      updatedVisas = updatedVisas.filter((visa) => {
        const visaDate = new Date(visa.createdAt);
        return visaDate.getFullYear() === parseInt(yearFilter);
      });
    }

    // Search filter
    if (searchTerm) {
      updatedVisas = updatedVisas.filter((visa) =>
        `${visa.applicantName} ${visa.applicantSurname} ${visa.passportNumber} ${visa.country}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    // Sorting
    if (sortBy === "newest") {
      updatedVisas.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
    } else if (sortBy === "oldest") {
      updatedVisas.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );
    } else if (sortBy === "highest-price") {
      updatedVisas.sort((a, b) => b.totalPrice - a.totalPrice);
    } else if (sortBy === "lowest-price") {
      updatedVisas.sort((a, b) => a.totalPrice - b.totalPrice);
    }

    setFilteredVisas(updatedVisas);
  }, [visas, statusFilter, monthFilter, yearFilter, searchTerm, sortBy, months]);

  const handleSelectAll = () => {
    if (isSelectAllChecked) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredVisas.map((visa) => visa.id));
    }
    setIsSelectAllChecked(!isSelectAllChecked);
  };

  const handleRowSelect = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const handleCheck = (id) => {
    window.location.href = `/visa/${id}`;
  };

  const handlePaid = (visa) => {
    setPaymentTarget(visa);
    setPaymentModalOpen(true);
  };

  const submitPayment = async (payload) => {
    if (!paymentTarget) return;
    setPaymentSubmitting(true);
    try {
      await createPayement(payload);
      await putVisa(paymentTarget.id, { ...paymentTarget, ispayed: true });

      const updatedVisas = await getAllvisas();
      setVisas(updatedVisas);
      setFilteredVisas(updatedVisas);

      setPaymentModalOpen(false);
      setPaymentTarget(null);
      swal("Visa payé!", { icon: "success" });
    } catch (error) {
      console.error("Error processing payment:", error);
      swal("Erreur", "Impossible de traiter le paiement", "error");
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const handleDelete = async (visaData) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this VISA",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    })
    .then(async (willDelete) => {
      if (willDelete) {
        try {
          // Call the delVisa function to delete the visa on the server
          await delVisa(visaData.id);

          // Refetch the visas to ensure the list is up to date
          await fetchVisas();

          // Clear selected rows and select all checkbox
          setSelectedRows([]);
          setIsSelectAllChecked(false);

          swal("VISA has been deleted!", {
            icon: "success",
          });
        } catch (error) {
          console.error("Error deleting visa:", error);
          swal("Error!", "Could not delete the VISA.", "error");
        }
      } else {
        swal("Deletion canceled");
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        header: () => (
          <FormGroup check className="font-size-16">
            <Label check>
              <Input
                type="checkbox"
                id="checkAll"
                checked={isSelectAllChecked}
                onChange={handleSelectAll}
              />
            </Label>
          </FormGroup>
        ),
        accessorKey: "id",
        cell: (cellProps) => (
          <FormGroup check className="font-size-16">
            <Label check>
              <Input
                type="checkbox"
                checked={selectedRows.includes(cellProps.row.original.id)}
                onChange={() => handleRowSelect(cellProps.row.original.id)}
              />
            </Label>
          </FormGroup>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Passenger Name",
        accessorKey: "applicantName",
        cell: (cellProps) => `${cellProps.row.original.applicantName} ${cellProps.row.original.applicantSurname}`,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Passport No",
        accessorKey: "passportNumber",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Destination",
        accessorKey: "country",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Created At",
        accessorKey: "createdAt",
        cell: (cellProps) => format(parseISO(cellProps.row.original.createdAt), 'MMMM dd, yyyy'),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const status = cellProps.row.original.status;
          let badgeColor = "";

          if (status === "ACCEPTED") badgeColor = "success";
          else if (status === "PENDING") badgeColor = "warning";
          else if (status === "REFUSED") badgeColor = "danger";

          return (
            <Badge color={badgeColor} className="font-size-10">
              {status}
            </Badge>
          );
        },
      },
      {
        header: "Payement",
        accessorKey: "ispayed",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => (
          <Badge
            color={
              cellProps.row.original.ispayed === true
                ? "success"  // Green for paid
                : "warning"  // Yellow/warning for unpaid
            }
            className="font-size-10"
          >
            {cellProps.row.original.ispayed ? "Paid" : "Not Paid"}
          </Badge>
        ),
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => (
          <div className="d-flex gap-3">
            <Link
              to="#"
              className="text-primary"
              onClick={() => handleCheck(cellProps.row.original.id)}
            >
              <i className="mdi mdi-eye font-size-18" id={`checktooltip-${cellProps.row.original.id}`} />
              <UncontrolledTooltip placement="top" target={`checktooltip-${cellProps.row.original.id}`}>
                Detail
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-danger"
              onClick={() => handleDelete(cellProps.row.original)}
            >
              <i className="mdi mdi-delete font-size-18" id={`deletetooltip-${cellProps.row.original.id}`} />
              <UncontrolledTooltip placement="top" target={`deletetooltip-${cellProps.row.original.id}`}>
                Delete
              </UncontrolledTooltip>
            </Link>
            {!cellProps.row.original.ispayed && (
              <Link
                to="#"
                className="text-success"
                id={`paidtooltip-${cellProps.row.original.id}`}
                onClick={() => handlePaid(cellProps.row.original)}
              >
                <i className="mdi mdi-currency-usd font-size-18" />
                <UncontrolledTooltip
                  placement="top"
                  target={`paidtooltip-${cellProps.row.original.id}`}
                >
                  Check payment
                </UncontrolledTooltip>
              </Link>
            )}
          </div>
        ),
      },
    ],
    [selectedRows, isSelectAllChecked]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={props.t("Visa Page")} breadcrumbItem={props.t("Visa Page")} />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <CardTitle style={{ marginBottom: "40px" }}>
                    {props.t("Visa Reservation")}
                  </CardTitle>

                  {/* Filters */}
                  <Row className="mb-3">
                    <Col md="3">
                      <FormGroup>
                        <Label for="statusFilter">Filter by Status</Label>
                        <Input
                          type="select"
                          id="statusFilter"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="all">All</option>
                          <option value="PENDING">Pending</option>
                          <option value="ACCEPTED">Accepted</option>
                          <option value="REFUSED">Refused</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="monthFilter">Filter by Month</Label>
                        <Input
                          type="select"
                          id="monthFilter"
                          value={monthFilter}
                          onChange={(e) => setMonthFilter(e.target.value)}
                        >
                          {months.map((month) => (
                            <option key={month} value={month}>
                              {month}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="yearFilter">Filter by Year</Label>
                        <Input
                          type="select"
                          id="yearFilter"
                          value={yearFilter}
                          onChange={(e) => setYearFilter(e.target.value)}
                        >
                          {years.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="sortBy">Sort by</Label>
                        <Input
                          type="select"
                          id="sortBy"
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                        >
                          <option value="default">Default</option>
                          <option value="newest">Most Recent</option>
                          <option value="oldest">Oldest</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Search Input */}
                  <Row className="mb-3">
                    <Col md="12">
                      <FormGroup>
                        <Label for="searchInput">Search</Label>
                        <Input
                          type="text"
                          id="searchInput"
                          placeholder="Search by name, surname, passport, or country"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                      <Oval height={80} width={80} color="#556ee6" visible={true} ariaLabel="oval-loading" secondaryColor="#4fa94d" strokeWidth={2} strokeWidthSecondary={2} />
                    </div>
                  ) : filteredVisas.length === 0 ? (
                    <div className="text-center text-muted py-5 border border-light rounded-3">
                      <i className="mdi mdi-passport display-4 d-block mb-2" />
                      <h5 className="mt-3">Aucune demande de visa</h5>
                      <p className="mb-0">Aucun résultat ne correspond aux filtres.</p>
                    </div>
                  ) : (
                    <Row>
                      {filteredVisas
                        .slice((page - 1) * pageSize, page * pageSize)
                        .map((visa) => {
                          const statusColor =
                            visa.status === "ACCEPTED" ? "success"
                            : visa.status === "PENDING" ? "warning"
                            : "danger";
                          const stampLabel =
                            visa.status === "ACCEPTED" ? "APPROUVÉ"
                            : visa.status === "PENDING" ? "EN COURS"
                            : "REFUSÉ";
                          return (
                            <Col xl={4} lg={6} md={6} key={visa.id}>
                              {/* ── PASSPORT-PAGE CARD — cream background + visa stamp ── */}
                              <Card
                                className="border h-100 overflow-hidden position-relative"
                                style={{
                                  background:
                                    "linear-gradient(135deg, #fef9ed 0%, #fef3e0 100%)",
                                  borderColor: "#e8d8a6 !important",
                                  boxShadow: "0 4px 14px rgba(245, 158, 11, 0.10)",
                                }}
                              >
                                {/* Diagonal "stamp" in the top-right */}
                                <div
                                  style={{
                                    position: "absolute",
                                    top: 24,
                                    right: -28,
                                    transform: "rotate(15deg)",
                                    border: `3px solid var(--bs-${statusColor})`,
                                    color: `var(--bs-${statusColor})`,
                                    padding: "4px 26px",
                                    fontSize: 11,
                                    fontWeight: 800,
                                    letterSpacing: 1,
                                    fontFamily: "monospace",
                                    background: "rgba(255,255,255,0.4)",
                                    zIndex: 2,
                                  }}
                                >
                                  {stampLabel}
                                </div>

                                <CardBody className="position-relative">
                                  {/* Passport header strip */}
                                  <div className="d-flex align-items-center mb-3 pb-2 border-bottom border-warning border-opacity-25">
                                    <i
                                      className="bx bx-id-card text-warning me-2"
                                      style={{ fontSize: 24 }}
                                    />
                                    <div>
                                      <small
                                        className="text-warning text-uppercase d-block font-size-10 fw-bold"
                                        style={{ letterSpacing: 1.5 }}
                                      >
                                        Demande de visa
                                      </small>
                                      <code className="font-size-11 text-muted">
                                        Réf. {visa.id?.slice(0, 8).toUpperCase()}
                                      </code>
                                    </div>
                                  </div>

                                  {/* Applicant name (passport-style) */}
                                  <div className="mb-3">
                                    <small
                                      className="text-muted text-uppercase d-block font-size-10"
                                      style={{ letterSpacing: 1 }}
                                    >
                                      Nom / Surname
                                    </small>
                                    <h5
                                      className="mb-0 text-dark text-uppercase"
                                      style={{ fontFamily: "monospace", letterSpacing: 1 }}
                                    >
                                      {(visa.applicantSurname || "—").toUpperCase()}
                                    </h5>
                                    <small
                                      className="text-muted text-uppercase d-block font-size-10 mt-2"
                                      style={{ letterSpacing: 1 }}
                                    >
                                      Prénom / Given name
                                    </small>
                                    <h6
                                      className="mb-0 text-dark"
                                      style={{ fontFamily: "monospace", letterSpacing: 0.5 }}
                                    >
                                      {(visa.applicantName || "—").toUpperCase()}
                                    </h6>
                                  </div>

                                  {/* Country + passport */}
                                  <div className="d-flex justify-content-between align-items-end pt-2 border-top border-warning border-opacity-25">
                                    <div style={{ minWidth: 0 }} className="me-2">
                                      <small className="text-muted text-uppercase d-block font-size-10" style={{ letterSpacing: 1 }}>
                                        Destination
                                      </small>
                                      <strong className="text-dark text-truncate d-block">
                                        {visa.country || "—"}
                                      </strong>
                                    </div>
                                    <div className="text-end">
                                      <small className="text-muted text-uppercase d-block font-size-10" style={{ letterSpacing: 1 }}>
                                        Passeport
                                      </small>
                                      <strong
                                        className="text-dark"
                                        style={{ fontFamily: "monospace" }}
                                      >
                                        {visa.passportNumber || "—"}
                                      </strong>
                                    </div>
                                  </div>

                                  {/* Footer — date + payment + actions */}
                                  <div className="border-top mt-3 pt-3 d-flex align-items-center justify-content-between">
                                    <div>
                                      <small className="text-muted d-block font-size-10">
                                        <i className="mdi mdi-calendar me-1" />
                                        {format(parseISO(visa.createdAt), "dd MMM yyyy")}
                                      </small>
                                      <Badge
                                        color={visa.ispayed ? "success" : "warning"}
                                        className="font-size-10 mt-1"
                                      >
                                        <i className={`mdi ${visa.ispayed ? "mdi-check-circle" : "mdi-clock-outline"} me-1`} />
                                        {visa.ispayed ? "Payé" : "Non payé"}
                                      </Badge>
                                    </div>
                                    <div className="d-flex gap-3">
                                      <Link
                                        to="#"
                                        className="text-primary"
                                        id={`cardview-${visa.id}`}
                                        onClick={() => handleCheck(visa.id)}
                                      >
                                        <i className="mdi mdi-eye font-size-18" />
                                        <UncontrolledTooltip placement="top" target={`cardview-${visa.id}`}>
                                          Détails
                                        </UncontrolledTooltip>
                                      </Link>
                                      <Link
                                        to="#"
                                        className="text-danger"
                                        id={`carddel-${visa.id}`}
                                        onClick={() => handleDelete(visa)}
                                      >
                                        <i className="mdi mdi-delete font-size-18" />
                                        <UncontrolledTooltip placement="top" target={`carddel-${visa.id}`}>
                                          Supprimer
                                        </UncontrolledTooltip>
                                      </Link>
                                      {!visa.ispayed && (
                                        <Link
                                          to="#"
                                          className="text-success"
                                          id={`cardpay-${visa.id}`}
                                          onClick={() => handlePaid(visa)}
                                        >
                                          <i className="mdi mdi-currency-usd font-size-18" />
                                          <UncontrolledTooltip placement="top" target={`cardpay-${visa.id}`}>
                                            Enregistrer le paiement
                                          </UncontrolledTooltip>
                                        </Link>
                                      )}
                                    </div>
                                  </div>
                                </CardBody>
                              </Card>
                            </Col>
                          );
                        })}
                    </Row>
                  )}
                  {!isLoading && filteredVisas.length > 0 && (
                    <CardPagination
                      page={page}
                      pageSize={pageSize}
                      total={filteredVisas.length}
                      onPageChange={setPage}
                      onPageSizeChange={setPageSize}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        <PaymentFormModal
          isOpen={paymentModalOpen}
          toggle={() => setPaymentModalOpen(false)}
          type="VISA"
          linkId={paymentTarget?.id}
          sourceLabel={
            paymentTarget
              ? `Visa ${paymentTarget.applicantName || ""} ${paymentTarget.applicantSurname || ""}`.trim()
              : ""
          }
          prefill={{
            firstname: paymentTarget?.applicantName || "",
            lastname: paymentTarget?.applicantSurname || "",
            phonenumber: paymentTarget?.user?.phone || "",
            email: paymentTarget?.emailAddress || "",
            amount: paymentTarget?.totalPrice || "",
          }}
          submitting={paymentSubmitting}
          onSubmit={submitPayment}
        />
      </div>
    </React.Fragment>
  );
};

VisaPage.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(VisaPage);